import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { HighlightVariant } from '../../../../../components/FileViewer/pdf-viewer/highlight-layer/HighlightLayer.props';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';

export type CitationContextType = {
  activeCitation?: CitationReferenceData;
  /** Highlight color for the active citation; 'ai' (AI-purple) while it is pending review. */
  activeCitationVariant: HighlightVariant;
  /**
   * Set (or clear) the active citation. `variant` controls the PDF highlight color;
   * callers derive it from the CITATION'S review status (statusToHighlightVariant,
   * SCAL-8986) - pending -> 'ai', anything else -> the default plain-yellow 'citation'.
   */
  setActiveCitation: (citation?: CitationReferenceData, variant?: HighlightVariant) => void;
}

const CitationContext = createContext<CitationContextType>({
  activeCitationVariant: 'citation',
  setActiveCitation: () => {},
});

export const useCitationContext = () => useContext(CitationContext);

const CitationProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeCitation, setActiveCitationState] = useState<CitationReferenceData | undefined>(undefined);
  const [activeCitationVariant, setActiveCitationVariant] = useState<HighlightVariant>('citation');

  const handleSetActiveCitation = useCallback(
    (citation?: CitationReferenceData, variant: HighlightVariant = 'citation') => {
      setActiveCitationState(citation);
      setActiveCitationVariant(variant);
    },
    []
  );

  const value = useMemo(
    () => ({ activeCitation, activeCitationVariant, setActiveCitation: handleSetActiveCitation }),
    [activeCitation, activeCitationVariant, handleSetActiveCitation]
  );

  return <CitationContext.Provider value={value}>{children}</CitationContext.Provider>;
};

export default CitationProvider;
